// score-thresholds-allow-file: the 5-tier colour scale itself, drawn as an axis; no API field serves it
import { colors, cx } from '@vizit/theme';
import type { ScoreDiagramProps } from './types';

/**
 * Score visualization diagram showing where the score falls on a 0-100 scale
 * Displays 5 segments (0-20, 20-40, 40-60, 60-80, 80-100) with different colors
 * and a marker indicating the score position
 */
export function ScoreDiagram(props: ScoreDiagramProps) {
  const { score } = props;

  // Ensure score is within 0-100 range
  const normalizedScore = Math.min(100, Math.max(0, score));

  const ranges = [
    {
      min: 0,
      max: 20,
      label: '0',
      color: 'bg-error-700',
      bgColor: 'bg-error-200',
      markerBorderColor: colors.error['700'],
      barWidth: '40.7px',
    },
    {
      min: 20,
      max: 40,
      label: '20',
      color: 'bg-error-500',
      bgColor: 'bg-error-100',
      markerBorderColor: colors.error['500'],
      barWidth: '40.7px',
    },
    {
      min: 40,
      max: 60,
      label: '40',
      color: 'bg-warning-400',
      bgColor: 'bg-warning-100',
      markerBorderColor: colors.warning['400'],
      barWidth: '41.8px',
    },
    {
      min: 60,
      max: 80,
      label: '60',
      color: 'bg-green-yellow-400',
      bgColor: 'bg-green-yellow-100',
      markerBorderColor: colors['green-yellow']['400'],
      barWidth: '40.7px',
    },
    {
      min: 80,
      max: 100,
      label: '80',
      color: 'bg-green-400',
      bgColor: 'bg-green-100',
      markerBorderColor: colors.green['400'],
      barWidth: '41.8px',
    },
  ];

  // Find which range the score falls into
  const activeRangeIndex = ranges.findIndex((range) => normalizedScore >= range.min && normalizedScore < range.max);
  const finalRangeIndex = normalizedScore === 100 ? ranges.length - 1 : activeRangeIndex;

  return (
    <div className="py-8" role="img" aria-label={`Score of ${normalizedScore} out of 100`}>
      <div className="flex items-center pr-2">
        {ranges.map((range, index) => {
          const isActive = index === finalRangeIndex;
          const shouldShowMarker = isActive && normalizedScore >= range.min;

          // When score is exactly 100, don't highlight the "80" label
          const isLabelActive = normalizedScore === 100 ? false : isActive;

          return (
            <div key={range.label} className="flex items-start pr-2">
              <div className="flex flex-col gap-2 items-center mr-[-4px]">
                <div
                  className={cx(
                    'flex items-center justify-center h-10 w-10 text-[10.5px]',
                    range.bgColor,
                    isLabelActive ? 'opacity-100' : 'opacity-30',
                  )}
                >
                  <span className={cx('text-tertiary', range.label === '40' && 'text-warning-800')}>{range.label}</span>
                </div>

                {/* Vertical divider line */}
                <div className="w-full flex justify-center items-start h-12">
                  <div className="w-0 h-full border-l border-dashed border-border-secondary" />
                </div>
              </div>

              <div className="flex flex-col items-start mr-[-4px] pt-20 pb-0 px-0" style={{ width: range.barWidth }}>
                <div className="relative w-full">
                  <div
                    className={cx('h-4 rounded-[1px]', range.color, isActive ? 'opacity-100' : 'opacity-30')}
                    style={{ width: range.barWidth }}
                  />

                  {shouldShowMarker && (
                    <div
                      className="absolute top-[-4px] w-2 h-7 bg-primary rounded-full border"
                      style={{
                        left: `${((normalizedScore - range.min) / (range.max - range.min)) * 100}%`,
                        transform: 'translateX(-50%)',
                        borderColor: range.markerBorderColor,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="flex flex-col gap-2 items-center">
          <div
            className={cx(
              'flex items-center justify-center h-10 w-10 text-[10.5px]',
              'bg-green-100',
              normalizedScore === 100 ? 'opacity-100' : 'opacity-30',
            )}
          >
            <span className="text-tertiary">100</span>
          </div>
          <div className="w-full flex justify-center items-start h-12">
            <div className="w-0 h-full border-l border-dashed border-border-secondary" />
          </div>
        </div>
      </div>
    </div>
  );
}
